import { auth } from '../config/firebase';
import { onAuthStateChanged } from 'firebase/auth';

// Utility to check if token is expired or about to expire (within 5 minutes)
export const isTokenExpired = (decodedToken) => {
  if (!decodedToken || !decodedToken.exp) return true;
  
  // Calculate token expiration (exp is in seconds, Date.now() is in milliseconds)
  const expirationTime = decodedToken.exp * 1000;
  const currentTime = Date.now();
  
  // Token is considered expired if it's within 5 minutes of expiration
  const fiveMinutesInMs = 5 * 60 * 1000;
  return currentTime + fiveMinutesInMs >= expirationTime;
};

// Parse JWT token without requiring a library
export const parseJwt = (token) => {
  try {
    const base64Url = token.split('.')[1];
    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
    const jsonPayload = decodeURIComponent(
      atob(base64)
        .split('')
        .map(c => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
        .join('')
    );
    return JSON.parse(jsonPayload);
  } catch (error) {
    console.error('Error parsing JWT:', error);
    return null;
  }
};

// Get a fresh token if the current one is expired or about to expire
export const refreshTokenIfNeeded = async () => {
  try {
    const currentUser = auth.currentUser;
    if (!currentUser) return null;
    
    const storedToken = localStorage.getItem('authToken');
    
    // If no token stored or token is expired/about to expire, get a fresh one
    if (!storedToken || isTokenExpired(parseJwt(storedToken))) {
      // Force refresh the token
      const freshToken = await currentUser.getIdToken(true);
      localStorage.setItem('authToken', freshToken);
      return freshToken;
    }
    
    return storedToken;
  } catch (error) {
    console.error('Token refresh error:', error);
    return null;
  }
};

// Set up token refresh to run periodically
export const setupTokenRefresh = () => {
  // Auto-refresh token every 10 minutes
  const refreshInterval = 10 * 60 * 1000; // 10 minutes
  
  // Set up interval for token refresh
  const intervalId = setInterval(async () => {
    try {
      if (auth.currentUser) {
        await refreshTokenIfNeeded();
      } else {
        // Clear interval if user is not logged in
        clearInterval(intervalId);
      }
    } catch (error) {
      console.error('Error in token refresh interval:', error);
    }
  }, refreshInterval);
  
  // Also refresh when auth state changes
  onAuthStateChanged(auth, async (user) => {
    if (user) {
      await refreshTokenIfNeeded();
    }
  });
  
  return intervalId;
};
